"use client"

import { useMemo } from 'react'
import Favicon, { isDomainLike } from '../Favicon'

interface HorizontalChartProps {
  labels: string[]
  values: number[]
  maxItems?: number
  className?: string
  onBarClick?: (label: string) => void
  title?: string
  valueLabel?: string
  showHeader?: boolean
  showFavicons?: boolean
  /** underline = orange under-bar (detail tabs); row = muted full-row bar (Overview) */
  variant?: 'underline' | 'row'
}

export default function ShadcnHorizontalChart({ 
  labels = [], 
  values = [], 
  maxItems = 5,
  className = '',
  onBarClick,
  title,
  valueLabel = 'Visitors',
  showHeader = true,
  showFavicons = true,
  variant = 'underline',
}: HorizontalChartProps) {
  const chartData = useMemo(() => {
    if (!labels.length || !values.length) return []

    const total = values.reduce((sum, v) => sum + v, 0)
    
    const combinedData = labels
      .map((label, index) => {
        const value = values[index] || 0
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0'
        
        return {
          name: label,
          value,
          fullLabel: label,
          percentage
        }
      })
      .filter(item => 
        item.fullLabel && 
        item.fullLabel.trim() !== '' && 
        !item.fullLabel.includes('{') && 
        !item.fullLabel.includes('}') &&
        item.value > 0
      )

    return combinedData
      .sort((a, b) => b.value - a.value)
      .slice(0, maxItems)
  }, [labels, values, maxItems])

  const handleClick = (data: any) => {
    if (onBarClick && data?.fullLabel) {
      onBarClick(data.fullLabel)
    }
  }

  if (!chartData.length) {
    return (
      <div className={`h-[200px] flex items-center justify-center text-muted-foreground ${className}`}>
        <div className="text-center">
          <div className="text-xs">No data to display</div>
          <div className="text-xs mt-1 opacity-70">Try a different date range.</div>
        </div>
      </div>
    )
  }

  const maxVal = Math.max(...chartData.map(d => d.value))

  return (
    <div className={`w-full flex flex-col gap-2 ${className}`}>
      {/* Column Headers */}
      {showHeader && (
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground/60 px-2 pb-1 border-b border-border/20">
          <span>{title || 'Name'}</span>
          <div className="flex items-center gap-3">
            <span>{valueLabel}</span>
            <span className="w-12 text-right">Share</span>
          </div>
        </div>
      )}
      
      {chartData.map((item, index) => {
        const shouldShowFavicon = showFavicons && isDomainLike(item.fullLabel)
        const shareWidth = `${(item.value / maxVal) * 100}%`

        if (variant === 'row') {
          return (
            <div
              key={index}
              className="group relative flex items-center justify-between gap-3 cursor-pointer px-2 py-2 -mx-2 rounded-sm transition-colors duration-150 overflow-hidden"
              onClick={() => handleClick(item)}
            >
              <div
                className="absolute inset-y-0 left-0 rounded-sm bg-muted/40 dark:bg-muted/50 group-hover:bg-muted/55 dark:group-hover:bg-muted/70 transition-colors"
                style={{ width: shareWidth }}
              />
              <div className="relative z-10 flex items-center gap-2 min-w-0 max-w-[65%]">
                {shouldShowFavicon && (
                  <Favicon
                    domain={item.fullLabel}
                    size={16}
                    className="opacity-90 group-hover:opacity-100 transition-opacity"
                    fallbackClassName="opacity-70"
                  />
                )}
                <span className="text-xs font-medium text-foreground truncate" title={item.fullLabel}>
                  {item.name}
                </span>
              </div>
              <div className="relative z-10 flex items-center gap-3 shrink-0">
                <span className="text-xs text-foreground font-medium tabular-nums">
                  {item.value.toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">
                  {item.percentage}%
                </span>
              </div>
            </div>
          )
        }
        
        return (
          <div 
            key={index} 
            className="group flex flex-col gap-1.5 cursor-pointer hover:bg-muted/30 px-2 py-1.5 -mx-2 rounded-md transition-colors duration-150"
            onClick={() => handleClick(item)}
          >
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0 max-w-[65%]">
                {shouldShowFavicon && (
                  <Favicon 
                    domain={item.fullLabel} 
                    size={16} 
                    className="opacity-90 group-hover:opacity-100 transition-opacity"
                    fallbackClassName="opacity-70"
                  />
                )}
                <span className="font-medium text-foreground truncate" title={item.fullLabel}>
                  {item.name}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-foreground font-semibold tabular-nums">
                  {item.value.toLocaleString()}
                </span>
                <span className="text-muted-foreground w-12 text-right tabular-nums">
                  {item.percentage}%
                </span>
              </div>
            </div>
            
            <div className="h-1 w-full bg-muted/30 rounded-full overflow-hidden group-hover:bg-muted/50 transition-colors duration-150">
              <div
                className="h-full bg-chart-1/40 rounded-full transition-all duration-500 ease-out group-hover:bg-chart-1/60"
                style={{ width: shareWidth }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
